export type PersistedTimeFormat = 'am' | 'pm' | '24h';

export type PersistedTimeFormatOptions = {
  storageKey: string;
  fallbackFormat?: PersistedTimeFormat;
  legacyStorageKey?: string;
};

const DEFAULT_FALLBACK_FORMAT: PersistedTimeFormat = '24h';

export function getPersistedTimeFormat({
  storageKey,
  fallbackFormat = DEFAULT_FALLBACK_FORMAT,
  legacyStorageKey,
}: PersistedTimeFormatOptions): PersistedTimeFormat {
  if (typeof sessionStorage === 'undefined') {
    return fallbackFormat;
  }

  const persistedTimeFormat = sessionStorage.getItem(storageKey);
  if (
    persistedTimeFormat === 'am' ||
    persistedTimeFormat === 'pm' ||
    persistedTimeFormat === '24h'
  ) {
    return persistedTimeFormat;
  }

  if (!legacyStorageKey) {
    return fallbackFormat;
  }

  const legacyPersistedFormat = sessionStorage.getItem(legacyStorageKey);
  if (legacyPersistedFormat === '24h') {
    return '24h';
  }
  if (legacyPersistedFormat === '12h') {
    return 'am';
  }

  return fallbackFormat;
}

export function setPersistedTimeFormat(
  storageKey: string,
  timeFormat: PersistedTimeFormat,
): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  sessionStorage.setItem(storageKey, timeFormat);
}

export type ResolveInitialTimeFormatOptions = PersistedTimeFormatOptions & {
  hourCycle?: 12 | 24;
};

/**
 * Pick 12h vs 24h for a datetime picker from an `hourCycle` when given (the
 * Iconik setting wins), otherwise fall back to the persisted preference. The
 * 12h case returns `am` only as a mode marker; the actual meridiem is derived
 * from the value when the picker opens (see `resolveOpenTimeFormat`).
 */
export function resolveInitialTimeFormat({
  hourCycle,
  ...persistedOptions
}: ResolveInitialTimeFormatOptions): PersistedTimeFormat {
  if (hourCycle === 24) {
    return '24h';
  }
  if (hourCycle === 12) {
    return 'am';
  }
  return getPersistedTimeFormat(persistedOptions);
}
